/**
 * The five settings areas of the webmail (information architecture 2026-10-28): Account & security, Mail,
 * Calendar & contacts, Rules & filters and Appearance. An area with more than one view shows them as
 * sections (a segmented control under the tab bar, never a second tab row). Every section keeps its own
 * deep-linkable page under /settings.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { PageTabs, SectionLinks } from '@platform/ui';

export type SettingsTab = 'signatures' | 'automatic-replies' | 'regional' | 'my-lists' | 'security' | 'inbox-rules' | 'calendar' | 'mail-encryption';

interface SettingsSection { id: SettingsTab; label: MessageKey; href: string }
interface SettingsArea { id: string; label: MessageKey; sections: readonly [SettingsSection, ...SettingsSection[]] }

export const SETTINGS_AREAS: readonly SettingsArea[] = [
  { id: 'account', label: 'ia.webmailAreas.account', sections: [
    { id: 'security', label: 'mfa.security', href: '/settings/security' },
    { id: 'mail-encryption', label: 'mailCrypto.title', href: '/settings/mail-encryption' },
  ] },
  { id: 'mail', label: 'ia.webmailAreas.mail', sections: [
    { id: 'signatures', label: 'webmail.settingsTabs.signatures', href: '/settings' },
    { id: 'automatic-replies', label: 'webmail.settingsTabs.automaticReplies', href: '/settings/automatic-replies' },
  ] },
  { id: 'calendar', label: 'ia.webmailAreas.calendar', sections: [{ id: 'calendar', label: 'webmail.calendarUx.settings.title', href: '/settings/calendar' }] },
  { id: 'rules', label: 'ia.webmailAreas.rules', sections: [
    { id: 'inbox-rules', label: 'inboxRules.title', href: '/settings/inbox-rules' },
    { id: 'my-lists', label: 'scopedLists.tab', href: '/settings/my-lists' },
  ] },
  { id: 'appearance', label: 'ia.webmailAreas.appearance', sections: [{ id: 'regional', label: 'webmail.regional.title', href: '/settings/regional' }] },
];

export function SettingsTabs({ active }: { active: SettingsTab }) {
  const { t } = useI18n();
  const area = SETTINGS_AREAS.find((candidate) => candidate.sections.some((section) => section.id === active)) ?? SETTINGS_AREAS[0]!;
  return (
    <>
      <PageTabs
        label={t('webmail.settingsTabs.label')}
        activeId={area.id}
        tabs={SETTINGS_AREAS.map((entry) => ({ id: entry.id, label: t(entry.label), href: entry.sections[0].href }))}
      />
      {area.sections.length > 1 && (
        <div className="px-page py-2">
          <SectionLinks label={t('ia.webmailAreas.sections')} activeId={active} sections={area.sections.map((section) => ({ id: section.id, label: t(section.label), href: section.href }))} />
        </div>
      )}
    </>
  );
}
